import { useState, useCallback, useMemo } from 'react';
import { NumberKeyState, MButtonState, ABPoints } from '../types';

// 數字鍵配置接口
interface NumberKeyConfig {
  maxNumberKeys: number;
  keySetIndex: number;
  ppPlayDuration: number;
  playbackRate: number;
  showTimecode: boolean;
  isUserLoggedIn: boolean;
}

// 數字鍵事件處理器接口
interface NumberKeyHandlers {
  onNumberKeyClick: (keyNo: number) => void;
  onDeleteModeToggle: () => void;
  onSettings: () => void;
  onMButtonClick: () => void;
  onModeChange: (newMode: MButtonState) => void;
  onNoteToggle: () => void;
  onCheckClick: () => void;
  onDeleteButtonClick: () => void;
  onDeleteButtonPress: (e: React.MouseEvent) => void;
  onDeleteButtonRelease: (e: React.MouseEvent) => void;
  onBookmarkClick: () => void;
  onSwitchClick: () => void;
  onBButtonClick: () => void;
  onSpeedChange: (speed: number) => void;
  onThemeDialogOpen?: (key: number) => void;
  onOpenNoteRecords?: () => void;
  onRequestLogin?: () => void;
}

// Hook 返回類型
interface UseNumberKeysReturn {
  // 狀態
  numberKeys: Record<string, NumberKeyState>;
  isDeleteMode: boolean;
  mButtonState: MButtonState;
  vbids: ABPoints[];
  
  // 配置
  config: NumberKeyConfig;
  
  // 事件處理器
  handlers: NumberKeyHandlers;
  
  // 狀態更新函數
  setNumberKeys: (keys: Record<string, NumberKeyState>) => void;
  setIsDeleteMode: (mode: boolean) => void;
  setMButtonState: (state: MButtonState) => void;
  setVbids: (vbids: ABPoints[]) => void;
  
  // 工具函數
  getSButtonStyle: () => any;
}

/**
 * 數字鍵管理 Hook
 * 統一管理數字鍵的狀態、配置和事件處理
 */
export const useNumberKeys = (
  initialConfig: Partial<NumberKeyConfig> = {},
  initialHandlers: Partial<NumberKeyHandlers> = {}
): UseNumberKeysReturn => {
  // 默認配置
  const defaultConfig: NumberKeyConfig = {
    maxNumberKeys: 18,
    keySetIndex: 0,
    ppPlayDuration: 0,
    playbackRate: 1,
    showTimecode: true,
    isUserLoggedIn: false,
    ...initialConfig
  };

  // 狀態管理
  const [numberKeys, setNumberKeys] = useState<Record<string, NumberKeyState>>({});
  const [isDeleteMode, setIsDeleteMode] = useState<boolean>(false);
  const [mButtonState, setMButtonState] = useState<MButtonState>('M');
  const [vbids, setVbids] = useState<ABPoints[]>([]);

  // 配置對象
  const config = useMemo(() => defaultConfig, [defaultConfig]);

  // 優化的事件處理器 - 使用 useCallback 避免不必要的重渲染
  const handleNumberKeyClick = useCallback((keyNo: number) => {
    console.log('🔘 數字鍵點擊:', keyNo, '刪除模式:', isDeleteMode);
  }, [isDeleteMode]);

  const handleDeleteModeToggle = useCallback(() => {
    console.log('🗑️ 切換刪除模式:', !isDeleteMode);
    setIsDeleteMode(!isDeleteMode);
  }, [isDeleteMode]);

  const handleModeChange = useCallback((newMode: MButtonState) => {
    console.log('🔄 模式切換:', newMode);
    setMButtonState(newMode);
  }, []);

  const handleDeleteButtonPress = useCallback((e: React.MouseEvent) => {
    console.log('🗑️ 刪除按鈕按下');
  }, []);

  const handleDeleteButtonRelease = useCallback((e: React.MouseEvent) => {
    console.log('🗑️ 刪除按鈕釋放');
  }, []);

  const handleSpeedChange = useCallback((speed: number) => {
    console.log('⚡ 速度變更:', speed);
  }, []);

  const handleThemeDialogOpen = useCallback((key: number) => {
    console.log('🎨 主題對話框:', key);
  }, []);

  // 默認事件處理器
  const defaultHandlers: NumberKeyHandlers = useMemo(() => ({
    onNumberKeyClick: handleNumberKeyClick,
    onDeleteModeToggle: handleDeleteModeToggle,
    onSettings: () => console.log('⚙️ 設定'),
    onMButtonClick: () => console.log('🎯 M 按鈕點擊'),
    onModeChange: handleModeChange,
    onNoteToggle: () => console.log('📝 筆記切換'),
    onCheckClick: () => console.log('✅ 檢查點擊'),
    onDeleteButtonClick: () => console.log('🗑️ 刪除按鈕點擊'),
    onDeleteButtonPress: handleDeleteButtonPress,
    onDeleteButtonRelease: handleDeleteButtonRelease,
    onBookmarkClick: () => console.log('🔖 書籤點擊'),
    onSwitchClick: () => console.log('🔄 切換點擊'),
    onBButtonClick: () => console.log('⏪ B 按鈕點擊'),
    onSpeedChange: handleSpeedChange,
    onThemeDialogOpen: handleThemeDialogOpen,
    onOpenNoteRecords: () => console.log('📋 開啟筆記記錄'),
    onRequestLogin: () => console.log('🔐 請求登入'),
    ...initialHandlers
  }), [
    handleNumberKeyClick,
    handleDeleteModeToggle,
    handleModeChange,
    handleDeleteButtonPress,
    handleDeleteButtonRelease,
    handleSpeedChange,
    handleThemeDialogOpen,
    initialHandlers
  ]);

  // 事件處理器對象
  const handlers = useMemo(() => defaultHandlers, [defaultHandlers, isDeleteMode]);

  // 工具函數
  const getSButtonStyle = useCallback(() => {
    return {
      minWidth: '2.2rem',
      width: '2.2rem',
      height: '2.2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#ffffff',
      border: '1px solid #e0e0e0',
      borderRadius: '50%',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: '#f5f5f5',
        borderColor: '#d0d0d0',
        transform: 'scale(1.05)'
      }
    };
  }, []);

  return {
    // 狀態
    numberKeys,
    isDeleteMode,
    mButtonState,
    vbids,
    
    // 配置
    config,
    
    // 事件處理器
    handlers,
    
    // 狀態更新函數
    setNumberKeys,
    setIsDeleteMode,
    setMButtonState,
    setVbids,
    
    // 工具函數
    getSButtonStyle
  };
};

export default useNumberKeys;
